import { useCallback, useState } from "react";
import moment from "moment";

/**
 * Generic reusable export hook for paginated list endpoints.
 *
 * Required configs:
 * - fetchListFn: (params) => Promise<{ data: any[] } | any[]>
 * - fields: Array<{ api: string, content?: string, header?: string }>
 * - translation: i18n t function (used if content provided)
 * - onExportExcel: ExportFile().onExportExcel
 * - setUrlForModalDirectLink: from ModalDirectLink context
 *
 * Optional configs:
 * - pageSize: number (default 100)
 * - mapRecord: (item, index) => mappedRow (default just adds index and formats createdAt)
 * - buildInformationColumn: (ctx) => string[][] header rows
 *   ctx = { translation, startDate, endDate, user, extra }
 * - getFieldExportLabel: (field) => string header label
 * - nameFile: string (default "data.xlsx")
 * - extra: any (pass-through to buildInformationColumn)
 */
export default function useExportExcel({
  fetchListFn,
  fields,
  translation,
  onExportExcel,
  setUrlForModalDirectLink,
  pageSize = 100,
  user,
  mapRecord,
  buildInformationColumn,
  getFieldExportLabel,
  nameFile = "data.xlsx",
  extra,
}) {
  const [percent, setPercent] = useState(0);
  const [isModalProgress, setIsModalProgress] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const fetchExportData = useCallback(
    async (pageIndex, filter) => {
      const safeFilter = { ...filter };
      Object.keys(safeFilter).forEach((k) => {
        if (!safeFilter[k]) delete safeFilter[k];
      });

      const response = await fetchListFn({
        ...safeFilter,
        limit: pageSize,
        skip: pageIndex * pageSize,
      });

      // Support both { data } and array responses
      const data = Array.isArray(response) ? response : response?.data;
      return data || [];
    },
    [fetchListFn, pageSize]
  );

  const handleExportExcel = useCallback(
    async ({
      dataLength,
      filters,
      sorter,
      searchValue,
      startDate,
      endDate,
      extraFilterParams,
    }) => {
      try {
        setIsExporting(true);
        const number = Math.max(1, Math.ceil((dataLength || 0) / pageSize));
        const params = Array.from({ length: number }, (_, i) => i);

        const percentPlus = 100 / params.length;
        setPercent(0);
        setIsModalProgress(true);

        let _counter = 0;
        let results = [];
        // Pull page-by-page until empty
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const dataFilter = {
            filter: {
              ...(filters || {}),
            },
            order: {
              ...(sorter || {}),
            },
            ...(searchValue && { searchText: searchValue }),
            ...(startDate && {
              startDate: moment(startDate)
                .set("HH", 0)
                .set("mm", 0)
                .set("ss", 0),
            }),
            ...(endDate && {
              endDate: moment(endDate)
                .set("HH", 23)
                .set("mm", 59)
                .set("ss", 59),
            }),
            ...(extraFilterParams || {}),
          };

          const page = await fetchExportData(_counter++, dataFilter);
          if (page && page.length > 0) {
            setPercent((prev) => prev + percentPlus);
            results = results.concat(page);
          } else {
            break;
          }
        }

        const mapped = results.map((item, index) =>
          mapRecord
            ? mapRecord(item, index)
            : {
                ...item,
                index: index + 1,
                createdAt: item?.createdAt
                  ? moment(item?.createdAt).format("DD/MM/YYYY HH:mm:ss")
                  : "",
              }
        );

        setTimeout(() => {
          setIsModalProgress(false);
          setPercent(0);

          onExportExcel({
            fieldApi: fields.map((f) => f.api),
            fieldExport: fields.map((f) =>
              getFieldExportLabel
                ? getFieldExportLabel(f)
                : f.header
                ? f.header
                : f.content
                ? translation(f.content)
                : f.api
            ),
            data: mapped,
            informationColumn:
              (buildInformationColumn &&
                buildInformationColumn({
                  translation,
                  startDate,
                  endDate,
                  user,
                  extra,
                })) || [],
            timeWait: 0,
            nameFile,
            setUrlForModalDirectLink,
          });
        }, 1000);
      } finally {
        setIsExporting(false);
      }
    },
    [
      fetchExportData,
      fields,
      onExportExcel,
      pageSize,
      setUrlForModalDirectLink,
      translation,
      user,
      mapRecord,
      buildInformationColumn,
      getFieldExportLabel,
      nameFile,
      extra,
    ]
  );

  return {
    handleExportExcel,
    percent,
    isModalProgress,
    setIsModalProgress,
    isExporting,
  };
}
